import React, { useState, useEffect } from 'react';
import { adminService, AdminAssistant } from '../services/admin-service';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, ChevronLeft, ChevronRight, Search, TrendingUp } from 'lucide-react';
import { Input } from './ui/input';

export default function AdminAssistantsList() {
  const [assistants, setAssistants] = useState<AdminAssistant[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const limit = 20;

  const loadAssistants = async (page: number) => {
    try {
      setLoading(true);
      const data = await adminService.getAssistants(page, limit);
      setAssistants(data.assistants);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Erro ao carregar agentes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssistants(currentPage);
  }, [currentPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Filtrar agentes por busca e ordenar por mensagens
  const filteredAssistants = assistants
    .filter(asst => 
      asst.name.toLowerCase().includes(search.toLowerCase()) ||
      asst.tenant_name.toLowerCase().includes(search.toLowerCase()) ||
      asst.ai_provider.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => b.total_messages - a.total_messages);

  const getProviderColor = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'openai': return 'bg-green-100 text-green-800';
      case 'anthropic': return 'bg-purple-100 text-purple-800';
      case 'google': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPlanBadge = (plan: string) => {
    return plan === 'premium' 
      ? <Badge className="bg-yellow-100 text-yellow-800">Premium</Badge>
      : <Badge variant="outline">Free</Badge>;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Nunca usado';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Hoje';
    if (diffDays === 1) return 'Ontem';
    if (diffDays < 7) return `${diffDays} dias atrás`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} semanas atrás`;
    return date.toLocaleDateString('pt-BR');
  };

  const formatCost = (cost: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(cost);
  };

  if (loading && assistants.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Agentes</h2>
          <p className="text-sm text-gray-600">
            {total} agente{total !== 1 ? 's' : ''} no total • Ordenado por maior uso
          </p>
        </div>
        <Button onClick={() => loadAssistants(currentPage)} disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Atualizando...
            </>
          ) : (
            'Atualizar'
          )}
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <Input
          type="text"
          placeholder="Buscar por nome, tenant ou provedor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Agentes</p>
              <p className="text-2xl font-bold text-gray-900">{total}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-600" />
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Mensagens</p>
              <p className="text-2xl font-bold text-gray-900">
                {assistants.reduce((sum, a) => sum + a.total_messages, 0).toLocaleString()}
              </p>
            </div>
            <div className="text-3xl">💬</div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Conversas</p>
              <p className="text-2xl font-bold text-gray-900">
                {assistants.reduce((sum, a) => sum + a.total_conversations, 0).toLocaleString()}
              </p>
            </div>
            <div className="text-3xl">💭</div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Custo Total</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCost(assistants.reduce((sum, a) => sum + a.total_cost, 0))}
              </p>
            </div>
            <div className="text-3xl">💰</div>
          </div>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tenant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Provedor
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mensagens
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conversas
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Custo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Último Uso
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAssistants.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    {search ? 'Nenhum agente encontrado' : 'Nenhum agente cadastrado'}
                  </td>
                </tr>
              ) : (
                filteredAssistants.map((assistant) => (
                  <tr key={assistant.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{assistant.name}</p>
                        {assistant.description && (
                          <p className="text-sm text-gray-500 truncate max-w-xs">
                            {assistant.description}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">{assistant.model_id}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{assistant.tenant_name}</p>
                        <div className="mt-1">{getPlanBadge(assistant.tenant_plan)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={getProviderColor(assistant.ai_provider)}>
                        {assistant.ai_provider.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm font-medium text-gray-900">
                        {assistant.total_messages.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm text-gray-600">
                        {assistant.total_conversations.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm font-medium text-gray-900">
                        {formatCost(assistant.total_cost)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">
                        {formatDate(assistant.last_used || '')}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Página {currentPage} de {totalPages} • {filteredAssistants.length} de {total} agentes
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
              >
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
              >
                Próxima
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

